/**
 * Converts [{ x, y }, ...] into uPlot-aligned series: [xs[], ys[]].
 * Non-finite y values become null so uPlot leaves gaps instead of drawing spikes.
 */
export function linePointsToUplotData(linePoints) {
  const xs = [];
  const ys = [];
  for (let i = 0; i < linePoints.length; i += 1) {
    const pt = linePoints[i];
    xs.push(pt.x);
    const y = pt.y;
    ys.push(typeof y === 'number' && Number.isFinite(y) ? y : null);
  }
  return [xs, ys];
}

/**
 * Applies a time offset to raw line points (used for sensor sync sliders).
 * Negative offset moves the series earlier on the video axis; positive, later.
 */
export function shiftLinePoints(linePoints, offsetSec) {
  if (!offsetSec) return linePoints;
  return linePoints.map((pt) => ({ ...pt, x: pt.x + offsetSec }));
}

export function resolveVideoDurationSec(videoDuration, videoRef) {
  if (typeof videoDuration === "number" && videoDuration > 0 && !Number.isNaN(videoDuration)) {
    return videoDuration;
  }
  const fromEl = videoRef?.current?.duration;
  if (typeof fromEl === "number" && fromEl > 0 && !Number.isNaN(fromEl)) {
    return fromEl;
  }
  return null;
}

export function buildLinePointsFromMetricSeries(
  metricSeries,
  videoDuration,
  videoRef,
  resolveHz,
  sampleRateHz
) {
  if (!metricSeries?.values?.length) return [];
  const fixedHz =
    typeof sampleRateHz === 'number' && Number.isFinite(sampleRateHz) && sampleRateHz > 0
      ? sampleRateHz
      : null;
  const videoDur = resolveVideoDurationSec(videoDuration, videoRef);
  const hz = fixedHz != null
    ? fixedHz
    : resolveHz(
      metricSeries,
      metricSeries.values.length,
      videoDur
    );
  return metricSeries.values.map((data, idx) => ({
    x: idx / hz,
    y: data[0],
  }));
}

/**
 * Positions the playback marker over the plotting area.
 *
 * The marker is a DOM element rather than something painted into the canvas:
 * following the video would otherwise force a full canvas redraw of every chart
 * on every frame, which is enough main-thread work to stall video playback.
 */
export function positionPlaybackMarker(u, el, t, thicknessDenom) {
  if (!u || !el) return;

  if (typeof t !== 'number' || !Number.isFinite(t)) {
    el.style.display = 'none';
    return;
  }

  const x = u.valToPos(t, 'x');
  const width = u.over.clientWidth;
  if (!Number.isFinite(x) || x < 0 || x > width) {
    el.style.display = 'none';
    return;
  }

  const { min, max } = u.scales.x;
  const xRange =
    Number.isFinite(min) && Number.isFinite(max) ? max - min : width;

  el.style.display = 'block';
  el.style.width = `${Math.max(1, xRange / thicknessDenom)}px`;
  el.style.transform = `translateX(${x}px)`;
}

/**
 * Time under a pointer event on the plot overlay.
 * Clamped to the current x scale so a drag past the edge stays on the trial.
 * Returns null when the plot cannot convert the pointer into a time.
 */
export function timeFromChartClientX(plot, clientX) {
  if (!plot || typeof plot.posToVal !== 'function' || !plot.over) return null;
  const rect = plot.over.getBoundingClientRect();
  if (!rect || !(rect.width > 0)) return null;
  const time = plot.posToVal(clientX - rect.left, 'x');
  if (typeof time !== 'number' || !Number.isFinite(time)) return null;
  const min = plot.scales && plot.scales.x ? plot.scales.x.min : undefined;
  const max = plot.scales && plot.scales.x ? plot.scales.x.max : undefined;
  if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
    return Math.min(max, Math.max(min, time));
  }
  return time;
}

/**
 * Nearest finite sample to a cursor index. Gaps (null y) are skipped so the
 * popup stays on the drawn curve.
 */
export function nearestFinitePoint(xs, ys, idx) {
  if (!Array.isArray(xs) || !Array.isArray(ys) || ys.length === 0) return null;
  if (!Number.isInteger(idx)) return null;
  const start = Math.max(0, Math.min(ys.length - 1, idx));
  for (let dist = 0; dist < ys.length; dist += 1) {
    const candidates = dist === 0 ? [start] : [start - dist, start + dist];
    for (let i = 0; i < candidates.length; i += 1) {
      const at = candidates[i];
      if (at < 0 || at >= ys.length) continue;
      const y = ys[at];
      if (typeof y !== 'number' || !Number.isFinite(y)) continue;
      const x = xs[at];
      return {
        index: at,
        x: typeof x === 'number' && Number.isFinite(x) ? x : null,
        y,
      };
    }
  }
  return null;
}

/** Print the y value, with the axis unit when the chart has one. */
export function formatPointTooltip(value, axisTitle) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const abs = Math.abs(value);
  const digits = abs !== 0 && abs < 0.01 ? 4 : abs >= 100 ? 1 : 2;
  const text = value.toFixed(digits);
  const unit = typeof axisTitle === 'string' ? axisTitle.trim() : '';
  return unit ? `${text} ${unit}` : text;
}

/**
 * Keep the value popup beside the point, flipping above/below and left/right
 * when the point is near the plot edge.
 */
export function placePointTooltip(pointX, pointY, tipWidth, tipHeight, plotWidth, plotHeight) {
  const gap = 8;
  const width = Number.isFinite(tipWidth) && tipWidth > 0 ? tipWidth : 0;
  const height = Number.isFinite(tipHeight) && tipHeight > 0 ? tipHeight : 0;
  let left = pointX + gap;
  let top = pointY - height - gap;
  if (Number.isFinite(plotWidth) && left + width > plotWidth) left = pointX - width - gap;
  if (top < 0) top = pointY + gap;
  if (left < 0) left = 0;
  if (Number.isFinite(plotHeight) && top + height > plotHeight) {
    top = Math.max(0, plotHeight - height);
  }
  return { left: Math.round(left), top: Math.round(top) };
}

export function scalesDifferFromFullRange(scale, fullMin, fullMax, tolerance = 1e-4) {
  if (!scale || !Number.isFinite(fullMin) || !Number.isFinite(fullMax)) {
    return false;
  }
  const { min, max } = scale;
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return false;
  }
  return (
    Math.abs(min - fullMin) > tolerance || Math.abs(max - fullMax) > tolerance
  );
}
